import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

/**
 * Don't strand a user on a dead detail page. When a single-resource detail query
 * fails with 404 — the resource was deleted (possibly by another user), or the
 * URL is stale/inaccessible — this toasts once and runs `onGone()` (navigate to
 * a list, or a view's own `onBack`) instead of leaving the user on a "not found"
 * dead-end they have to escape manually.
 *
 * This is the REACTIVE counterpart to the goal detail's realtime `deleted`
 * listener, for the many resources whose backend emits no delete event: the
 * redirect fires whenever the 404 surfaces (on refetch or navigation).
 *
 * @param {object} query   a React Query result ({ isError, error })
 * @param {() => void} onGone  recovery action (e.g. () => navigate('/list'))
 * @param {string} [message]   toast text (British English, ends with a full stop)
 * @param {number[]} [statuses]  HTTP statuses that count as "gone" (default
 *   `[404]`). Pass e.g. `[403]` for a detail page where LOSING access (the
 *   resource still exists, but this user no longer can see it — e.g. a goal
 *   follower who was just removed) should bounce the same way a 404 does,
 *   rather than stranding them on a raw access-denied message.
 */
export function useRedirectOnNotFound(query, onGone, message, statuses = [404]) {
  // `fired` is only ever touched INSIDE the effect (never during render), so the
  // redirect runs at most once per hit. Re-running the effect when an inline
  // `onGone`/`message` changes identity is harmless — the guard blocks a repeat.
  const fired = useRef(false)
  const isError = Boolean(query?.isError)
  const status = query?.error?.response?.status ?? query?.error?.status
  // Derive a stable boolean so the effect deps don't churn on the inline
  // `statuses` array's identity (its VALUE only changes with the status).
  const match = isError && statuses.includes(status)
  useEffect(() => {
    if (match && !fired.current) {
      fired.current = true
      if (message) toast(message)
      onGone()
    } else if (!isError) {
      // Reset once the query recovers so a later hit can act again.
      fired.current = false
    }
  }, [match, isError, onGone, message])
}
